import * as React from "react";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IEssResidue, IResidue } from "./types";
import { ProteinRepresentationType } from "@loschmidt/molstar";
import IResultInhibitor from "../JobPage/interfaces/frontend/IResultInhibitor";
import { JobInfoContext } from "../../job-info-context";
import { AssociatedDrug, ViewerAnnotations } from "../../api/openapi-generator";
import { JobApi, ProteinApi } from "../../api";
import { deriveInhibitors } from "../JobPage/context/use-frontend-results";
import { useLocation } from "react-router-dom";

type ViewerContextType = ReturnType<typeof useProviderValue>;

const ViewerContext = createContext<ViewerContextType | undefined>(undefined);

export const useViewerContext = () => {
  const context = useContext(ViewerContext);
  if (context === undefined) {
    throw new Error("useViewerContext must be used within a ViewerContextProvider");
  }
  return context;
};

export function ViewerContextProvider({
  children,
}: {
  children: ReactNode,
}) {
  const value = useProviderValue();
  return (
    <ViewerContext.Provider value={value}>
      {children}
    </ViewerContext.Provider>
  );
}

function useProviderValue() {

  // Context:

  const {
    jobInfo,
  } = useContext(JobInfoContext);

  // State:

  const [annotations, setAnnotations] = useState<ViewerAnnotations | null>(null);
  const [associatedDrugs, setAssociatedDrugs] = useState<AssociatedDrug[] | null>(null);

  const [wtEnabled, setWtEnabled] = useState(true);
  const [mutEnabled, setMutEnabled] = useState(true);

  const [wtMutRes, setWtMutRes] = useState(true);
  const [mutMutRes, setMutMutRes] = useState(true);

  const [wtEssRes, setWtEssRes] = useState(true);
  const [mutEssRes, setMutEssRes] = useState(true);

  const [wtPockets, setWtPockets] = useState(false);
  const [mutPockets, setMutPockets] = useState(false);

  const [representation, setRepresentation] = useState<ProteinRepresentationType>('cartoon');

  const [inhibitorModalOpen, setInhibitorModalOpen] = useState(false);

  // Memos:

  const essentialResidues = useMemo<IEssResidue[]>(() => {
    if (!annotations) return [];
    const mutatedResidues = (annotations.mutatedPdbPositions ?? [])
      .flatMap(g => g.residues.map(r => ({
        chain: g.chain,
        position: r.position,
      })));
    return annotations.essentialResidues
      .flatMap(g => g.residues.map(r => {
        const isMutated = mutatedResidues.some(m => g.chain === m.chain && r.position === m.position);
        return {
          ...r,
          key: `${g.chain}_${r.position}`,
          chain: g.chain,
          mutatedAA: isMutated ? annotations.mutation.mut : '',
        };
      }));
  }, [annotations]);

  const mutatedResidues = useMemo<IResidue[]>(() => {
    return (annotations?.mutatedPdbPositions ?? [])
      .flatMap(g => g.residues.map(r => ({
        ...r,
        chain: g.chain,
      })));
  }, [annotations]);

  const inhibitorsBE = annotations?.inhibitors;

  const inhibitors = useMemo(() => {
    return deriveInhibitors(inhibitorsBE ?? null, associatedDrugs ?? null);
  }, [inhibitorsBE, associatedDrugs]);

  // Callbacks:

  const toggleInhibitorModal = useCallback(() => setInhibitorModalOpen(inhibitorModal => !inhibitorModal), []);

  // Custom hooks:
  const inhibitorSelection = useInhibitorSelection();

  // Effects:

  useEffect(() => {
    if (jobInfo && jobInfo.status === 5) {
      JobApi.viewer({ id: jobInfo.id })
        .then(setAnnotations)
        .catch(err => console.error(err));
      ProteinApi.getAssociatedDrugs({codeAscii: jobInfo.request.protein})
        .then(setAssociatedDrugs)
        .catch(err => console.error(err));
    }
  }, [jobInfo]);

  usePreselectInhibitorsEffect(inhibitors, inhibitorSelection.setSelectedInhibitors);

  return {
    annotations,
    wtEnabled, setWtEnabled,
    mutEnabled, setMutEnabled,
    wtMutRes, setWtMutRes,
    mutMutRes, setMutMutRes,
    wtEssRes, setWtEssRes,
    mutEssRes, setMutEssRes,
    wtPockets, setWtPockets,
    mutPockets, setMutPockets,
    representation, setRepresentation,
    essentialResidues, mutatedResidues,
    inhibitorModalOpen, toggleInhibitorModal,
    inhibitors,
    ...inhibitorSelection,
  }
}

type SelectedInhibitorsSetter = (updater: IResultInhibitor[] | ((prevSelectedInhibitors: IResultInhibitor[]) => IResultInhibitor[])) => void;

function useInhibitorSelection() {

  // State:

  const [state, setState] = useState<{
    selectedInhibitors: IResultInhibitor[],
    wtInhibitors: Record<string, boolean>,
    mutInhibitors: Record<string, boolean>,
  }>({
    selectedInhibitors: [],
    wtInhibitors: {},
    mutInhibitors: {},
  });

  // Callbacks:

  const setSelectedInhibitors: SelectedInhibitorsSetter = useCallback(updater => {
    setState(state => {
      const selectedInhibitors = typeof updater === 'function' ? updater(state.selectedInhibitors) : updater;
      return {
        selectedInhibitors: selectedInhibitors,
        wtInhibitors: Object.fromEntries(selectedInhibitors.map(i => [i.id, state.wtInhibitors[i.id] ?? true])),
        mutInhibitors: Object.fromEntries(selectedInhibitors.map(i => [i.id, state.mutInhibitors[i.id] ?? true])),
      }
    });
  }, [setState]);

  const setWtInhibitor = useCallback((id: string, value: boolean) => {
    setState(state => {
      return {
        ...state,
        wtInhibitors: {
          ...state.wtInhibitors,
          [id]: value,
        },
      }
    })
  }, [setState]);

  const setMutInhibitor = useCallback((id: string, value: boolean) => {
    setState(state => {
      return {
        ...state,
        mutInhibitors: {
          ...state.mutInhibitors,
          [id]: value,
        },
      }
    })
  }, [setState]);

  const setAllWtInhibitors = useCallback((value: boolean) => {
    setState(state => {
      const wtInhibitors = Object.fromEntries(Object.entries(state.wtInhibitors).map(([id, _]) => [id, value] as const));
      return {
        ...state,
        wtInhibitors,
      }
    })
  }, [setState]);

  const setAllMutInhibitors = useCallback((value: boolean) => {
    setState(state => {
      const mutInhibitors = Object.fromEntries(Object.entries(state.mutInhibitors).map(([id, _]) => [id, value] as const));
      return {
        ...state,
        mutInhibitors,
      }
    })
  }, [setState]);

  return {
    ...state,
    setSelectedInhibitors,
    setWtInhibitor,
    setMutInhibitor,
    setAllWtInhibitors,
    setAllMutInhibitors,
  }

}

function usePreselectInhibitorsEffect(
  inhibitors: IResultInhibitor[],
  setSelectedInhibitors: SelectedInhibitorsSetter,
) {
  const {search} = useLocation();
  useEffect(() => {
    const searchParsed = new URLSearchParams(search);
    const preselectedInhibitorZincId = searchParsed.get('inhibitor');
    if (preselectedInhibitorZincId !== null) {
      setSelectedInhibitors(inhibitors.filter(i => i.zincId === preselectedInhibitorZincId));
    } else {
      setSelectedInhibitors([]);
    }
  }, [search, inhibitors, setSelectedInhibitors]);
}
